import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import useCallbackRef from 'hooks/useCallbackRef';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Button, Loader, Modal, Select } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { EnterpriseBusinessUnit, EnterpriseMember } from 'models/Enterprises';
import api from 'api';
import { getUserFullName } from 'utils';
import RevertIcon from 'icons/Revert.icon';
import TrashIcon from 'icons/Trash.icon';

import './DeleteBusinessUnitModal.styles.scss';

export type DeleteBusinessUnit = {
  deleteEnterpriseMemberIds?: string[];
  updateEnterpriseMembers?: {
    memberId: string;
    businessUnitId: string;
  }[];
};

const INITIAL_DATA_FOR_SUBMIT: DeleteBusinessUnit = {
  deleteEnterpriseMemberIds: [],
  updateEnterpriseMembers: [],
};

type DeleteBusinessUnitModalProps = {
  className?: string;
  enterpriseBusinessUnitId: string;
  enterpriseMembersWithBusinessUnit: EnterpriseMember[];
  businessUnits: EnterpriseBusinessUnit[];
  updateBusinessUnits: (
    updatedBusinessUnits: EnterpriseBusinessUnit[],
    shouldRefetch?: boolean,
  ) => void;
} & ModalProps;

const DeleteBusinessUnitModal: React.ForwardRefRenderFunction<
  ModalRef,
  DeleteBusinessUnitModalProps
> = (props, ref) => {
  const {
    className,
    enterpriseBusinessUnitId,
    businessUnits,
    enterpriseMembersWithBusinessUnit = [],
    updateBusinessUnits,
    onClose,
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [dataForSubmit, setDataForSubmit] = useState<DeleteBusinessUnit>(
    INITIAL_DATA_FOR_SUBMIT,
  );

  const { popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('delete-business-unit-modal', className);

  const { t } = useTranslation();

  const validForSubmit = useMemo(
    () =>
      enterpriseMembersWithBusinessUnit.every(({ _id }) => {
        const { deleteEnterpriseMemberIds, updateEnterpriseMembers } =
          dataForSubmit;

        return (
          deleteEnterpriseMemberIds.includes(_id) ||
          updateEnterpriseMembers.some(({ memberId }) => memberId === _id)
        );
      }),
    [dataForSubmit, enterpriseMembersWithBusinessUnit],
  );

  const selectOptions = useMemo(
    () =>
      businessUnits
        .filter(({ _id }) => _id !== enterpriseBusinessUnitId)
        .map(({ _id, name }) => ({ value: _id, label: name })),
    [businessUnits, enterpriseBusinessUnitId],
  );

  const getSelectValue = useCallback(
    (mId: string) =>
      dataForSubmit.updateEnterpriseMembers.find(
        ({ memberId }) => memberId === mId,
      )?.businessUnitId || null,
    [dataForSubmit.updateEnterpriseMembers],
  );

  const getIsDeleted = useCallback(
    (mId: string) =>
      dataForSubmit.deleteEnterpriseMemberIds.some(
        (memberId) => memberId === mId,
      ),
    [dataForSubmit.deleteEnterpriseMemberIds],
  );

  const onSelectChange = useCallback(
    (memberId: string, businessUnitId: string) => {
      setDataForSubmit(
        ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
          deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.filter(
            (id) => id !== memberId,
          ),
          updateEnterpriseMembers: [
            ...updateEnterpriseMembers.filter(
              ({ memberId: id }) => id !== memberId,
            ),
            { memberId, businessUnitId },
          ],
        }),
      );
    },
    [],
  );

  const onRemove = useCallback((memberId: string) => {
    setDataForSubmit(
      ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
        deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.some(
          (id) => id === memberId,
        )
          ? deleteEnterpriseMemberIds.filter((id) => id !== memberId)
          : [...deleteEnterpriseMemberIds, memberId],
        updateEnterpriseMembers: [
          ...updateEnterpriseMembers.filter(
            ({ memberId: id }) => id !== memberId,
          ),
        ],
      }),
    );
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.enterprises.deleteBusinessUnit(
        enterpriseBusinessUnitId,
        dataForSubmit,
      );
      updateBusinessUnits(data);
      modal?.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    dataForSubmit,
    enterpriseBusinessUnitId,
    modal,
    popServerError,
    updateBusinessUnits,
  ]);

  const onCustomClose = useCallback(() => {
    setDataForSubmit(INITIAL_DATA_FOR_SUBMIT);
    onClose?.();
  }, [onClose]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('connectMembersAndBusinessUnit')}
      onClose={onCustomClose}
      {...rest}
    >
      {isLoading && <Loader />}

      <p className="description">
        {t('connectMembersAndBusinessUnitDescription')}
      </p>

      <div className="members-section">
        {enterpriseMembersWithBusinessUnit.map(
          ({ _id, firstName, lastName }) => {
            const isDeleted = getIsDeleted(_id);

            return (
              <div key={_id}>
                <p
                  className={classNames({
                    removed: isDeleted,
                  })}
                >
                  {getUserFullName(firstName, lastName)}
                </p>

                <Select
                  renderAsPortal
                  portalClassName="select-value-options"
                  value={getSelectValue(_id)}
                  placeholder={t('choose')}
                  options={selectOptions}
                  onChange={(e: any) => onSelectChange(_id, e.value)}
                />

                <Button
                  variant="icon"
                  icon={isDeleted ? RevertIcon : TrashIcon}
                  className={classNames({
                    'warning-action-button': !isDeleted,
                  })}
                  onClick={() => onRemove(_id)}
                />
              </div>
            );
          },
        )}
      </div>

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>

        <Button disabled={isLoading || !validForSubmit} onClick={onSubmit}>
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(DeleteBusinessUnitModal);
